import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { api } from '../api/client.js';
import {
  Badge, Breadcrumbs, Card, ConfidenceMeter, Empty, ErrorNotice, Loading, Notice, fmt,
} from '../components/ui.jsx';

const bucketColour = (bucket) => {
  if (bucket >= 90) return 'var(--ok)';
  if (bucket >= 70) return 'var(--warn)';
  return 'var(--danger)';
};

export default function AnalyticsPage() {
  const [insurerId, setInsurerId] = useState('');

  const insurers = useQuery({ queryKey: ['insurers'], queryFn: () => api.listInsurers().then((r) => r.data) });
  const dist = useQuery({
    queryKey: ['confidenceDistribution', insurerId],
    queryFn: () => api.confidenceDistribution(insurerId || undefined).then((r) => r.data),
  });
  const strength = useQuery({
    queryKey: ['attributeStrength', insurerId],
    queryFn: () => api.attributeStrength(insurerId || undefined).then((r) => r.data),
  });
  const risks = useQuery({ queryKey: ['disagreements'], queryFn: () => api.disagreements(100).then((r) => r.data) });

  const chartData = (dist.data || []).map((d) => ({
    name: `${d.bucket}-${d.bucket + 4}`,
    bucket: d.bucket,
    count: d.count,
  }));

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Analytics' }]} />
      <div className="page-head">
        <div>
          <h1>Match quality analytics</h1>
          <div className="page-sub">
            Where the confidence numbers come from, which attributes are carrying the decision, and which
            links look wrong enough to need attention.
          </div>
        </div>
        <div className="spacer" />
        <div className="f" style={{ minWidth: 200 }}>
          <label htmlFor="a-insurer">Insurer</label>
          <select id="a-insurer" value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
            <option value="">All insurers</option>
            {(insurers.data || []).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
      </div>

      <div className="cols cols-2">
        <Card title="Confidence histogram" subtitle="Current links in 5-point buckets">
          {dist.isLoading ? <Loading rows={5} />
            : chartData.length === 0 ? <Empty title="No scored links yet" />
              : (
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} stroke="var(--border)" interval={0} angle={-35} textAnchor="end" height={54} />
                      <YAxis tick={{ fontSize: 12, fill: 'var(--text-muted)' }} stroke="var(--border)" width={52} />
                      <Tooltip
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
                        formatter={(v) => [fmt(v), 'Records']}
                        labelFormatter={(l) => `Confidence ${l}%`}
                      />
                      <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                        {chartData.map((d) => <Cell key={d.bucket} fill={bucketColour(d.bucket)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
          <Notice tone="info" title="Reading this chart">
            A healthy run is heavily weighted to the right, with a thin tail. A spike at exactly 100 across a
            whole insurer usually means that insurer's text was the original source of the GIIB text, so the
            comparison is not independent.
          </Notice>
        </Card>

        <Card title="Attribute strength" subtitle="Average score each attribute contributed">
          {strength.isLoading ? <Loading rows={6} />
            : !strength.data?.sampleSize ? <Empty title="No scored links yet" />
              : (
                <>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr><th>Attribute</th><th style={{ minWidth: 160 }}>Average score</th><th className="num">Not comparable</th></tr>
                      </thead>
                      <tbody>
                        {strength.data.attributes.map((a) => (
                          <tr key={a.key}>
                            <td>{a.label}</td>
                            <td>
                              {a.avgScore === null ? <span className="prov">no data</span>
                                : <ConfidenceMeter value={a.avgScore} band={a.avgScore >= 90 ? 'high' : a.avgScore >= 70 ? 'medium' : 'low'} />}
                            </td>
                            <td className="num">{a.missing === undefined ? '-' : fmt(a.missing)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="prov" style={{ marginTop: 8 }}>
                    Based on {fmt(strength.data.sampleSize)} current links.
                  </div>
                </>
              )}
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card
          title="Links that need attention"
          subtitle="Band-check failures, and records where a re-run disagreed with the stored link"
          bodyClass="tight"
        >
          {risks.isLoading ? <div style={{ padding: 16 }}><Loading rows={5} /></div>
            : risks.error ? <div style={{ padding: 16 }}><ErrorNotice error={risks.error} /></div>
              : (risks.data || []).length === 0
                ? <Empty title="Nothing flagged">No band-check failures and no engine disagreements.</Empty>
                : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Insurer</th>
                          <th>Insurer record</th>
                          <th>Currently mapped to</th>
                          <th className="num">Confidence</th>
                          <th>Issue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(risks.data || []).map((r) => (
                          <tr key={r.mappingId}>
                            <td>{r.insurerCode}</td>
                            <td>
                              <div className="cell-main">{r.icVehicle}</div>
                              <div className="cell-sub">code {r.icCode}{r.icCc ? ` · ${r.icCc} cc` : ''}</div>
                            </td>
                            <td>
                              <div className="cell-main">{r.giibVehicle || <span className="prov">no vehicle</span>}</div>
                              <div className="cell-sub mono">{r.universalKey || '-'}{r.giibCc ? ` · ${r.giibCc} cc` : ''}</div>
                            </td>
                            <td className="num">{r.confidence.toFixed(1)}%</td>
                            <td>
                              <Badge kind={r.ratingCheck === 'fail' ? 'fail' : 'warn'}>
                                {r.ratingCheck === 'fail' ? 'band mismatch' : 'engine disagrees'}
                              </Badge>
                              <div className="prov" style={{ marginTop: 3, maxWidth: 340 }}>
                                {r.ratingNote || r.comment}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
        </Card>
      </div>
    </div>
  );
}
